import { BranchRuleKind } from '@/contracts/enums/branching';
import { QuestionType } from '@/contracts/enums/questionTypes';
import { BranchRules } from '@/contracts/models/survey-branching';
import { OptionItem } from '@/contracts/models/survey';
import { EditorQuestion } from '@/contracts/models/survey-editor';
import type { TemplateQuestion } from '@/features/survey-builder/components/tabs/DesignTab';
import { useTranslation } from '@/hooks/useTranslation';
import {
  makeDefaultChoices,
  makeDefaultMatrixColumns,
  makeDefaultMatrixRows,
} from '@/utils/surveyJsService';
import { pruneRulesAfterStructureChange } from '@/features/survey-builder/utils/branchingService';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useState } from 'react';
import { BranchRulesEditor } from './branching/BranchRulesEditor';
import { BranchTargetDropdown } from './branching/BranchTargetDropdown';
import { ChoiceSelector } from './ChoiceSelector';
import { MatrixSelector } from './MatrixSelector';
import { QuestionTypeSelector } from './QuestionTypeSelector';

const ratingScaleOptions = (maxValue: number) =>
  Array.from({ length: maxValue }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  }));

export interface QuestionFormProps {
  question: EditorQuestion;
  onChange: (question: Partial<EditorQuestion>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  disabled?: boolean;
  mixerQuestionId?: string;
  isVotingEnabled?: boolean;
  aiModerationEnabled?: boolean;
  onVotingEnabledChange?: (enabled: boolean) => void;
  onAiModerationEnabledChange?: (enabled: boolean) => void;
  lockStructure?: boolean;
  templateQuestion?: TemplateQuestion;
  /** Branch children get a reduced form: no Mixer type, no branching of their own */
  isBranchChild?: boolean;
  /** 1-based main-line number of this question (for branch target labels) */
  questionNumber?: number;
  isLastQuestion?: boolean;
}

export const QuestionForm: React.FC<QuestionFormProps> = ({
  question,
  onChange,
  onDelete,
  onDuplicate,
  disabled = false,
  mixerQuestionId,
  isVotingEnabled,
  aiModerationEnabled,
  onVotingEnabledChange,
  onAiModerationEnabledChange,
  lockStructure = false,
  templateQuestion,
  isBranchChild = false,
  questionNumber = 1,
  isLastQuestion = false,
}) => {
  const { t } = useTranslation();
  const [localQuestion, setLocalQuestion] = useState(question);
  // Track rating values separately to allow clearing without SurveyJS overwriting
  const [rateMinValue, setRateMinValue] = useState<number | undefined>(
    question.rateMin
  );
  const [rateMaxValue, setRateMaxValue] = useState<number | undefined>(
    question.rateMax
  );

  useEffect(() => {
    setLocalQuestion(question);
  }, [question]);

  // Only sync rating values from parent when question.id changes (different question selected)
  useEffect(() => {
    setRateMinValue(question.rateMin);
    setRateMaxValue(question.rateMax);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omit rateMin/rateMax
  }, [question.id]);

  const handleTypeChange = (type: QuestionType) => {
    const updates: Partial<EditorQuestion> = { type };

    // Clear type-specific properties when changing type
    if (
      type === QuestionType.RadioGroup ||
      type === QuestionType.Checkbox ||
      type === QuestionType.Dropdown ||
      type === QuestionType.MultiSelectDropdown
    ) {
      updates.choices = localQuestion.choices || makeDefaultChoices();
    } else {
      updates.choices = undefined;
    }

    if (type === QuestionType.Rating) {
      // Set default values when switching to rating type
      const defaultMin = localQuestion.rateMin ?? 1;
      const defaultMax = localQuestion.rateMax ?? 5;
      updates.rateMin = defaultMin;
      updates.rateMax = defaultMax;
      // Also update local state so the inputs display the values
      setRateMinValue(defaultMin);
      setRateMaxValue(defaultMax);
    } else {
      updates.rateMin = undefined;
      updates.rateMax = undefined;
    }

    if (type === QuestionType.Text) {
      updates.maxLength = 500; // Always set short text default
    } else if (type === QuestionType.Comment || type === QuestionType.Mixer) {
      updates.maxLength = 1000; // Always set long text default (Mixer is also long text)
    } else {
      updates.maxLength = undefined;
    }

    if (type === QuestionType.Matrix) {
      updates.rows = localQuestion.rows || makeDefaultMatrixRows();
      updates.columns = localQuestion.columns || makeDefaultMatrixColumns();
    } else {
      updates.rows = undefined;
      updates.columns = undefined;
    }

    setLocalQuestion(prev => ({ ...prev, ...updates }));
    onChange(updates);
  };

  const applyStructureChange = (
    patch: Partial<
      Pick<
        EditorQuestion,
        'choices' | 'rows' | 'columns' | 'rateMin' | 'rateMax'
      >
    >
  ) => {
    setLocalQuestion(prev => {
      const next = { ...prev, ...patch };
      if (!next.branchRules) return next;
      const pruned = pruneRulesAfterStructureChange(next.branchRules, {
        name: next.name,
        type: next.type,
        choices: next.choices?.map(o => o.value),
        rows: next.rows?.map(o => o.value),
        columns: next.columns?.map(o => o.value),
        rateMin: next.rateMin,
        rateMax: next.rateMax,
      });
      return pruned ? { ...next, branchRules: pruned } : next;
    });
    onChange(patch);
  };

  const handleChoicesChange = (choices: OptionItem[]) => {
    applyStructureChange({ choices });
  };

  const handleRatingChange = (
    field: 'rateMin' | 'rateMax',
    value: number | null
  ) => {
    let newValue = value ?? undefined;
    if (newValue !== undefined) {
      const max = field === 'rateMin' ? 19 : 20;
      newValue = Math.min(Math.max(newValue, 1), max);
    }
    if (field === 'rateMin') {
      setRateMinValue(newValue);
    } else {
      setRateMaxValue(newValue);
    }
    applyStructureChange({ [field]: newValue });
  };

  const handleRowsChange = (rows: OptionItem[]) => {
    applyStructureChange({ rows });
  };

  const handleColumnsChange = (columns: OptionItem[]) => {
    applyStructureChange({ columns });
  };

  const handleBranchRulesChange = (branchRules: BranchRules) => {
    setLocalQuestion(prev => ({ ...prev, branchRules }));
    onChange({ branchRules });
  };

  const showBranchingEditors =
    !isBranchChild && !lockStructure && !!localQuestion.branchRules;
  const choiceBranchRules =
    showBranchingEditors &&
    localQuestion.branchRules?.kind === BranchRuleKind.Choice
      ? localQuestion.branchRules
      : undefined;

  const choicesForRender = localQuestion.choices ?? makeDefaultChoices();

  const renderChoiceBranchTarget = choiceBranchRules
    ? (index: number) => {
        const choiceValue = choicesForRender[index]?.value ?? '';
        return (
          <BranchTargetDropdown
            value={choiceBranchRules.choiceTargets[choiceValue]}
            onChange={target =>
              handleBranchRulesChange({
                ...choiceBranchRules,
                choiceTargets: {
                  ...choiceBranchRules.choiceTargets,
                  [choiceValue]: target,
                },
              })
            }
            branchChildren={localQuestion.branchChildren ?? []}
            parentNumber={questionNumber}
            isLastQuestion={isLastQuestion}
            disabled={disabled}
            ariaLabel={t('editor.branching.choiceTargetAriaLabel', {
              number: index + 1,
            })}
          />
        );
      }
    : undefined;

  const isOpenEnded =
    localQuestion.type === QuestionType.Text ||
    localQuestion.type === QuestionType.Comment ||
    localQuestion.type === QuestionType.Mixer;

  const isMultipleChoice =
    localQuestion.type === QuestionType.RadioGroup ||
    localQuestion.type === QuestionType.Checkbox ||
    localQuestion.type === QuestionType.Dropdown ||
    localQuestion.type === QuestionType.MultiSelectDropdown;

  const isRating = localQuestion.type === QuestionType.Rating;
  const isMatrix = localQuestion.type === QuestionType.Matrix;

  const isBoolean = localQuestion.type === QuestionType.Boolean;

  return (
    <div className='space-y-4'>
      {/* CONTENT: Always visible; disabled prop controls editability */}
      <div
        className={
          lockStructure && (isOpenEnded || isRating) ? '' : 'min-h-[100px]'
        }
      >
        {isOpenEnded && !lockStructure && (
          <div className='space-y-3'>
            {localQuestion.type === QuestionType.Mixer && (
              <p className='text-sm text-gray-600'>
                {t('editor.mixer.disclaimer')}
              </p>
            )}

            <InputTextarea
              value=''
              placeholder={t('editor.fields.answerAreaPreviewPlaceholder')}
              disabled
              rows={4}
              className='w-full bg-gray-50'
              aria-label={t('editor.fields.answerAreaPreviewAriaLabel')}
            />
          </div>
        )}

        {/* Mixer voting/moderation — per-survey settings, visible for all mixer questions */}
        {localQuestion.type === QuestionType.Mixer && (
          <div className='flex flex-wrap items-center gap-4'>
            <div className='flex items-center gap-2'>
              <Checkbox
                inputId='allow-voting'
                checked={isVotingEnabled ?? true}
                onChange={e => onVotingEnabledChange?.(e.checked ?? false)}
                disabled={disabled}
                aria-label={t('editor.mixer.allowVotingAriaLabel')}
              />
              <label htmlFor='allow-voting' className='text-sm cursor-pointer'>
                {t('editor.mixer.allowVoting')}
              </label>
            </div>

            <div className='flex items-center gap-2'>
              <Checkbox
                inputId='ai-moderation'
                checked={aiModerationEnabled ?? false}
                onChange={e =>
                  onAiModerationEnabledChange?.(e.checked ?? false)
                }
                disabled={disabled}
                aria-label={t('editor.mixer.aiModerationAriaLabel')}
              />
              <label htmlFor='ai-moderation' className='text-sm cursor-pointer'>
                {t('editor.mixer.aiModeration')}
              </label>
            </div>
          </div>
        )}

        {/* Multiple Choice Options */}
        {isMultipleChoice && (
          <div>
            <ChoiceSelector
              choices={choicesForRender}
              onChange={handleChoicesChange}
              disabled={disabled}
              lockStructure={lockStructure}
              originalChoices={
                lockStructure && !disabled
                  ? templateQuestion?.choices
                  : undefined
              }
              questionType={localQuestion.type}
              renderItemSuffix={renderChoiceBranchTarget}
            />
          </div>
        )}

        {isRating && !lockStructure && (
          <div className='flex items-center gap-2 text-sm text-gray-500'>
            <span>
              {t('editor.fields.rateMin')}: {rateMinValue ?? '-'}
            </span>
            <span>-</span>
            <span>
              {t('editor.fields.rateMax')}: {rateMaxValue ?? '-'}
            </span>
          </div>
        )}

        {isBoolean && (!lockStructure || disabled) && (
          <div className='flex items-center gap-4 text-sm text-gray-600'>
            <div className='flex items-center gap-2'>
              <input
                type='radio'
                name='boolean-preview'
                disabled
                className='cursor-not-allowed'
                aria-label={t('editor.questionTypes.booleanYes')}
              />
              <span>{t('editor.questionTypes.booleanYes')}</span>
            </div>
            <div className='flex items-center gap-2'>
              <input
                type='radio'
                name='boolean-preview'
                disabled
                className='cursor-not-allowed'
                aria-label={t('editor.questionTypes.booleanNo')}
              />
              <span>{t('editor.questionTypes.booleanNo')}</span>
            </div>
          </div>
        )}
        {isBoolean && lockStructure && !disabled && (
          <div className='space-y-3'>
            <div className='flex items-center gap-3'>
              {templateQuestion && (
                <span className='text-sm text-gray-400 flex-shrink-0'>
                  {t('editor.template.original')}{' '}
                  {templateQuestion.labelTrue ||
                    t('editor.template.booleanYes')}
                </span>
              )}
              <InputText
                value={localQuestion.labelTrue || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLocalQuestion(prev => ({
                    ...prev,
                    labelTrue: e.target.value,
                  }));
                  onChange({ labelTrue: e.target.value });
                }}
                placeholder={
                  templateQuestion?.labelTrue || t('editor.template.booleanYes')
                }
                aria-label={t('editor.template.booleanYesAriaLabel')}
                className='flex-1'
              />
            </div>
            <div className='flex items-center gap-3'>
              {templateQuestion && (
                <span className='text-sm text-gray-400 flex-shrink-0'>
                  {t('editor.template.original')}{' '}
                  {templateQuestion.labelFalse ||
                    t('editor.template.booleanNo')}
                </span>
              )}
              <InputText
                value={localQuestion.labelFalse || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLocalQuestion(prev => ({
                    ...prev,
                    labelFalse: e.target.value,
                  }));
                  onChange({ labelFalse: e.target.value });
                }}
                placeholder={
                  templateQuestion?.labelFalse || t('editor.template.booleanNo')
                }
                aria-label={t('editor.template.booleanNoAriaLabel')}
                className='flex-1'
              />
            </div>
          </div>
        )}

        {/* Matrix Configuration */}
        {isMatrix && (
          <div>
            <MatrixSelector
              rows={localQuestion.rows || makeDefaultMatrixRows()}
              columns={localQuestion.columns || makeDefaultMatrixColumns()}
              onRowsChange={handleRowsChange}
              onColumnsChange={handleColumnsChange}
              disabled={disabled}
              lockStructure={lockStructure}
              originalRows={
                lockStructure && !disabled ? templateQuestion?.rows : undefined
              }
              originalColumns={
                lockStructure && !disabled
                  ? templateQuestion?.columns
                  : undefined
              }
            />
          </div>
        )}

        {/* Branching rules for boolean/rating/matrix (choice types configure
            targets inline on each choice row) */}
        {showBranchingEditors &&
          localQuestion.branchRules &&
          localQuestion.branchRules.kind !== BranchRuleKind.Choice && (
            <BranchRulesEditor
              question={localQuestion}
              rules={localQuestion.branchRules}
              questionNumber={questionNumber}
              isLastQuestion={isLastQuestion}
              onRulesChange={handleBranchRulesChange}
              disabled={disabled}
            />
          )}
      </div>

      {!lockStructure && (
        <div className='flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200'>
          <div className={`${isRating ? 'w-full sm:w-48' : 'w-48'}`}>
            <QuestionTypeSelector
              value={localQuestion.type}
              onChange={handleTypeChange}
              disabled={disabled}
              mixerQuestionId={mixerQuestionId}
              currentQuestionId={localQuestion.id}
              excludeMixer={isBranchChild}
            />
          </div>

          {isRating && (
            <div className='flex items-center gap-3 w-full sm:w-auto'>
              <Dropdown
                id='min-rating'
                value={rateMinValue ?? null}
                options={ratingScaleOptions(19)}
                onChange={e => handleRatingChange('rateMin', e.value ?? null)}
                disabled={disabled}
                aria-label={t('editor.fields.rateMinAriaLabel')}
                className='w-24 max-w-full'
                pt={{ input: { className: 'py-2 px-2 text-sm' } }}
              />
              <Dropdown
                id='max-rating'
                value={rateMaxValue ?? null}
                options={ratingScaleOptions(20)}
                onChange={e => handleRatingChange('rateMax', e.value ?? null)}
                disabled={disabled}
                aria-label={t('editor.fields.rateMaxAriaLabel')}
                className='w-24 max-w-full'
                pt={{ input: { className: 'py-2 px-2 text-sm' } }}
              />
            </div>
          )}

          <div
            className={`flex flex-wrap items-center gap-2 ${isRating ? 'w-full sm:w-auto sm:ml-auto' : 'ml-auto'}`}
          >
            {onDuplicate && (
              <>
                {question.type === QuestionType.Mixer && (
                  <Tooltip
                    target={`#duplicate-btn-${question.id}`}
                    position='top'
                    content={t('editor.actions.duplicateMixerDisabledTooltip')}
                  />
                )}
                <span id={`duplicate-btn-${question.id}`}>
                  <Button
                    icon='pi pi-copy'
                    label={t('editor.actions.duplicateQuestion')}
                    outlined
                    size='small'
                    onClick={onDuplicate}
                    disabled={disabled || question.type === QuestionType.Mixer}
                    severity='secondary'
                    className='border-black rounded-md bg-white text-black hover:bg-gray-50'
                    aria-label={
                      question.type === QuestionType.Mixer
                        ? `${t('editor.actions.duplicateQuestionAriaLabel')} - ${t('editor.actions.duplicateMixerDisabledTooltip')}`
                        : t('editor.actions.duplicateQuestionAriaLabel')
                    }
                  />
                </span>
              </>
            )}
            {onDelete && (
              <Button
                icon='pi pi-trash'
                label={t('editor.actions.delete')}
                outlined
                size='small'
                onClick={onDelete}
                disabled={disabled}
                severity='secondary'
                className='border-black rounded-md bg-white text-black hover:bg-gray-50'
                aria-label={t('editor.actions.deleteQuestionAriaLabel')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
